import { z } from 'zod';
import { GameRecordSchema, GameRecordUpdateSchema, GameSchema, GameViewSchema, LanguagesSchema } from './game';
import { UpdateUserRecordSchema, UserProfileSchema, UserRecordSchema, userRoles } from './user';
import { GameSkinSchema, SkinRecordSchema, SkinRecordUpdateSchema } from './skin';
import { VariantRecordSchema, VariantRecordUpdateSchema, VariantSchema } from './variant';
import { EventRecordSchema, EventRecordUpdateSchema, EventSchema, EventStatusSchema, EventViewSchema } from './event';
import { ChatMessageSchema, ClientInitSchema, RoundStateSchema, TaskStateRecordSchema, TeamRecordSchema } from './client';
import { MediaDirectorySchema, MediaNodeSchema } from './media';

export const WebAuthnCredentialSchema = z.object({
	id: z.string(),
	publicKey: z.string(), // we get it as a Uint8Array, but let's store it in base64 for convenience
	counter: z.number(),
	transports: z
		.array(z.enum(['ble', 'cable', 'hybrid', 'internal', 'nfc', 'smart-card', 'usb']))
		.nullable()
		.optional(),
});
export type WebAuthnCredential = z.infer<typeof WebAuthnCredentialSchema>;

export const MessageSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('empty-message') }),
	z.object({ kind: z.literal('text-message'), message: z.string() }),

	z.object({
		kind: z.literal('register'),
		role: z.enum(userRoles).default('user'),
		firstName: z.string(),
		lastName: z.string(),
		email: z.string(),
		password: z.string(),
	}),
	z.object({
		kind: z.literal('login-with-password'),
		email: z.string(),
		password: z.string(),
		['cf-turnstile-response']: z.string().optional(),
	}),
	z.object({ kind: z.literal('get-profile-info') }),
	z.object({ kind: z.literal('get-users') }),
	z.object({ kind: z.literal('get-user'), userId: z.string() }),
	z.object({ kind: z.literal('update-user'), userId: z.string(), data: UpdateUserRecordSchema }),
	z.object({ kind: z.literal('game-create'), game: GameSchema }),
	z.object({ kind: z.literal('game-list') }),
	z.object({ kind: z.literal('get-game-list') }),
	z.object({ kind: z.literal('game-find'), id: z.string() }),
	z.object({ kind: z.literal('game-update'), game: GameRecordUpdateSchema }),
	z.object({ kind: z.literal('game-delete'), id: z.string() }),
	z.object({ kind: z.literal('game-copy'), id: z.string() }),

	z.object({ kind: z.literal('variant-create'), variant: VariantSchema }),
	z.object({ kind: z.literal('variant-list') }),
	z.object({ kind: z.literal('get-variant-list') }),
	z.object({ kind: z.literal('variant-find'), id: z.string() }),
	z.object({ kind: z.literal('variant-update'), variant: VariantRecordUpdateSchema }),
	z.object({ kind: z.literal('variant-delete'), id: z.string() }),
	z.object({ kind: z.literal('variant-copy'), id: z.string() }),

	z.object({ kind: z.literal('event-create'), event: EventSchema }),
	z.object({ kind: z.literal('event-list'), includeArchived: z.boolean().default(false).optional() }),
	z.object({ kind: z.literal('get-event-list'), date: z.string().optional(), includeArchived: z.boolean().default(false).optional() }),
	z.object({ kind: z.literal('event-check-code'), code: z.string() }),
	z.object({ kind: z.literal('event-find'), id: z.string().optional(), code: z.string().optional() }),
	z.object({ kind: z.literal('event-update'), event: EventRecordUpdateSchema }),
	z.object({ kind: z.literal('event-delete'), id: z.string() }),
	z.object({ kind: z.literal('event-archive'), id: z.string(), archive: z.boolean().default(true) }),
	z.object({ kind: z.literal('get-event-overview'), id: z.string() }),
	z.object({ kind: z.literal('event-leave-overview'), id: z.string() }),

	z.object({ kind: z.literal('team-delete'), id: z.string(), eventId: z.string() }),

	z.object({ kind: z.literal('event-freeze'), eventId: z.string(), freeze: z.boolean().default(true) }),
	z.object({ kind: z.literal('event-set-status'), id: z.string(), status: EventStatusSchema }),
	z.object({ kind: z.literal('event-joinable'), code: z.string() }),
	z.object({ kind: z.literal('event-join'), code: z.string(), teamName: z.string(), deviceId: z.string().optional() }),
	z.object({ kind: z.literal('event-rejoin'), id: z.string(), teamId: z.string(), deviceId: z.string().optional() }),
	z.object({ kind: z.literal('update-team-points'), teamId: z.string(), eventId: z.string(), points: z.number() }),

	z.object({ kind: z.literal('event-register-members'), id: z.string(), teamId: z.string(), members: z.array(z.string()) }),
	z.object({ kind: z.literal('event-change-rat'), eventId: z.string(), teamId: z.string(), ratId: z.string() }),
	z.object({ kind: z.literal('event-change-captain'), eventId: z.string(), teamId: z.string(), captainId: z.string() }),
	z.object({ kind: z.literal('event-remove-device'), eventId: z.string(), teamId: z.string(), deviceId: z.string() }),
	z.object({ kind: z.literal('event-set-hide-score'), id: z.string(), hideScore: z.boolean() }),
	z.object({ kind: z.literal('event-chat-message'), message: ChatMessageSchema, eventId: z.string() }),
	z.object({
		kind: z.literal('event-update-team-location'),
		teamId: z.string(),
		eventId: z.string(),
		location: z.object({ lat: z.number(), lng: z.number(), heading: z.number().nullable(), timestamp: z.number().default(0) }),
	}),
	z.object({
		kind: z.literal('event-change-round-unlock'),
		roundIds: z.array(z.string()),
		eventId: z.string(),
		teamId: z.string(),
		unlocked: z.boolean(),
	}),
	z.object({ kind: z.literal('event-change-round-unlock-global'), roundId: z.string(), eventId: z.string(), unlocked: z.boolean() }),
	z.object({
		kind: z.literal('event-change-task-unlock'),
		taskIds: z.array(z.string()),
		eventId: z.string(),
		teamId: z.string(),
		unlocked: z.boolean(),
	}),
	z.object({
		kind: z.literal('event-change-task-unlock-global'),
		taskId: z.string(),
		eventId: z.string(),
		unlocked: z.boolean(),
	}),
	// z.object({ kind: z.literal('event-task-submission'), submission: TaskStateSchema, eventId: z.string(), teamId: z.string() }),
	z.object({
		kind: z.literal('event-task-review-submission'),
		eventId: z.string(),
		teamId: z.string(),
		taskId: z.string(),
		pointsGiven: z.number(),
		completionResponse: z.string().nullish(),
	}),
	z.object({
		kind: z.literal('event-messages-read'),
		eventId: z.string(),
		teamId: z.string().optional(),
		messagesCount: z.number(),
	}),
	z.object({ kind: z.literal('event-download-media'), eventId: z.string() }),

	z.object({ kind: z.literal('stats') }),

	z.object({ kind: z.literal('skin-create'), skin: GameSkinSchema }),
	z.object({ kind: z.literal('skin-list') }),
	z.object({ kind: z.literal('skin-find'), id: z.string() }),
	z.object({ kind: z.literal('skin-update'), skin: SkinRecordUpdateSchema }),
	z.object({ kind: z.literal('skin-delete'), id: z.string() }),
	z.object({ kind: z.literal('skin-copy'), id: z.string() }),

	// NOTE: uploads are handled with uploadFile() in `app-console/src/engine/api.ts`
	// so you will only find list/find/delete endpoints here
	z.object({ kind: z.literal('media-list-directory'), folderId: z.string().nullable() }),
	z.object({ kind: z.literal('media-create-folder'), folderName: z.string(), parentId: z.string().nullable() }),
	z.object({ kind: z.literal('media-delete'), nodeId: z.string() }),
	z.object({ kind: z.literal('media-move'), nodeIds: z.array(z.string()), targetFolderId: z.string().nullable() }),

	z.object({
		kind: z.literal('debug-state'),
		eventId: z.string(),
		teamId: z.string(),
		deviceId: z.string(),
		debug: z.any(),
		state: z.any(),
		time: z.number(),
	}),
]);
export type Message = z.infer<typeof MessageSchema>;

export const MessageReturnSchemas = {
	'empty-message': null,
	'text-message': null,

	'login-with-password': z.object({ success: z.boolean(), msg: z.string().optional(), token: z.string().optional() }),
	register: z.object({ success: z.boolean(), msg: z.string().optional() }),
	'get-profile-info': UserProfileSchema.or(z.object({ error: z.string() })),
	'get-users': z.array(UserRecordSchema).or(z.object({ error: z.string() })),
	'get-user': UserRecordSchema.or(z.object({ error: z.string() })),
	'update-user': z.object({ success: z.boolean() }),

	'game-create': z.object({ success: z.boolean(), game: GameSchema.nullable().optional() }),
	'game-list': z.object({ success: z.boolean(), games: z.array(GameRecordSchema).nullable().optional() }),
	'get-game-list': z.object({
		success: z.boolean(),
		languages: z.array(z.string()).optional(),
		gameViews: z.array(GameViewSchema).optional(),

		gameMap: z.record(z.string(), z.object({ id: z.string(), name: z.string(), language: z.string() })).optional(),
	}),
	'game-find': z.object({ success: z.boolean(), game: GameRecordSchema.nullable().optional() }),
	'game-update': z.object({ success: z.boolean(), game: GameRecordSchema.nullable().optional() }),
	'game-delete': z.object({ success: z.boolean() }),
	'game-copy': z.object({ success: z.boolean() }),

	'variant-create': z.object({ success: z.boolean(), variant: VariantSchema.nullable().optional() }),
	'variant-list': z.object({ success: z.boolean(), variants: z.array(VariantRecordSchema).nullable().optional() }),
	'get-variant-list': z.object({
		success: z.boolean(),
		languages: z.array(z.string()).optional(),
		gameViews: z
			.array(z.object({ id: z.string(), name: z.string(), createdAt: z.string().datetime(), updatedAt: z.string().datetime() }))
			.optional(),
		variantViews: z
			.array(
				z.object({
					id: z.string(),
					name: z.string(),
					gameId: z.string(),
					gameName: z.string(),
					type: z.string(),
					language: z.string(),
					skin: SkinRecordSchema.nullable(),
					createdAt: z.string().datetime(),
					updatedAt: z.string().datetime(),
				}),
			)
			.optional(),

		variantMap: z.record(z.string(), z.object({ id: z.string(), name: z.string() })).optional(),
		gameMap: z.record(z.string(), z.object({ id: z.string(), name: z.string(), language: z.string() })).optional(),
	}),
	'variant-find': z.object({ success: z.boolean(), variant: VariantRecordSchema.nullable().optional() }),
	'variant-update': z.object({ success: z.boolean(), variant: VariantRecordSchema.nullable().optional() }),
	'variant-delete': z.object({ success: z.boolean() }),
	'variant-copy': z.object({ success: z.boolean() }),

	'event-create': z.object({ success: z.boolean(), event: EventRecordSchema.nullable().optional() }),
	'event-list': z.object({ success: z.boolean(), events: z.array(EventRecordSchema).nullable().optional() }),
	'get-event-list': z.object({
		success: z.boolean(),
		eventViews: z.array(EventViewSchema).nullable().optional(),
		languages: z.array(z.string()).optional(),
		gameViews: z
			.array(z.object({ id: z.string(), name: z.string(), createdAt: z.string().datetime(), updatedAt: z.string().datetime() }))
			.optional(),
		variantViews: z
			.array(z.object({ id: z.string(), name: z.string(), createdAt: z.string().datetime(), updatedAt: z.string().datetime() }))
			.optional(),

		variantMap: z.record(z.string(), z.object({ id: z.string(), name: z.string() })).optional(),
		gameMap: z.record(z.string(), z.object({ id: z.string(), name: z.string(), language: z.string() })).optional(),
	}),
	'event-check-code': z.object({ available: z.boolean() }),
	'event-find': z.object({ success: z.boolean(), event: EventRecordSchema.nullable().optional() }),
	'event-update': z.object({ success: z.boolean(), event: EventRecordSchema.nullable().optional() }),
	'event-delete': z.object({ success: z.boolean() }),
	'event-archive': z.object({ success: z.boolean() }),
	'team-delete': z.object({ success: z.boolean() }),
	'get-event-overview': z.object({
		success: z.boolean(),
		event: EventRecordSchema.nullish(),
		game: GameRecordSchema.nullish(),
		teams: z.array(TeamRecordSchema).nullish(),
		chatMessages: z.array(ChatMessageSchema).nullish(),
		gameRoundStates: z.array(RoundStateSchema).nullish(),
		taskSubmissions: z.array(TaskStateRecordSchema).nullish(),
		skin: GameSkinSchema.nullish(),
		variant: VariantSchema.nullish(),
	}),
	'event-leave-overview': z.object({ success: z.boolean() }),

	'event-set-status': z.object({ success: z.boolean(), msg: z.string().optional() }),
	'event-joinable': z.object({
		joinable: z.boolean(),
		rejoin: z.boolean().optional(),
		spectateFunction: z.boolean().optional(),
		reason: z.string().optional(),
		eventId: z.string().optional(),
		teamName: z.string().optional(),
		teamId: z.string().optional(),
		skin: GameSkinSchema.optional(),
		language: LanguagesSchema.optional(),
		deviceId: z.string().optional(),
	}),
	'event-join': z.object({ success: z.boolean(), init: ClientInitSchema.optional(), msg: z.string().optional() }),
	'event-rejoin': z.object({ success: z.boolean(), init: ClientInitSchema.optional(), isCaptain: z.boolean().optional() }),
	'update-team-points': z.object({ success: z.boolean(), msg: z.string().optional(), newScore: z.number().optional() }),
	'event-register-members': z.object({
		success: z.boolean(),
		members: z.array(z.object({ name: z.string(), id: z.string() })).nullish(),
		ratId: z.string().nullish(),
	}),
	'event-change-rat': z.object({ success: z.boolean() }),
	'event-change-captain': z.object({ success: z.boolean() }),
	'event-remove-device': z.object({ success: z.boolean() }),
	'event-set-hide-score': null,
	'event-chat-message': null,
	'event-update-team-location': null,
	'event-change-round-unlock': null,
	'event-change-round-unlock-global': null,
	'event-change-task-unlock': null,
	'event-change-task-unlock-global': null,
	// 'event-task-submission': null,
	'event-task-review-submission': null,
	'event-messages-read': null,
	'event-download-media': z.object({ success: z.boolean(), downloadUrl: z.string().optional() }),

	stats: z.object({
		success: z.boolean(),
		eventsCount: z.number().optional(),
		teamsCount: z.number().optional(),
		gamesCount: z.number().optional(),
		variantsCount: z.number().optional(),
		skinsCount: z.number().optional(),
	}),

	'skin-create': z.object({ success: z.boolean() }),
	'skin-list': z.object({ success: z.boolean(), skins: z.array(SkinRecordSchema).nullable().optional() }),
	'skin-find': z.object({ success: z.boolean(), skin: SkinRecordSchema.nullable().optional() }),
	'skin-update': z.object({ success: z.boolean(), skin: SkinRecordSchema.nullable().optional() }),
	'skin-delete': z.object({ success: z.boolean() }),
	'skin-copy': z.object({ success: z.boolean() }),

	'media-list-directory': z.object({ success: z.boolean(), media: MediaDirectorySchema.optional() }),
	'media-create-folder': z.object({ success: z.boolean(), media: MediaNodeSchema.optional() }),
	'media-delete': z.object({ success: z.boolean() }),
	'media-move': z.object({ success: z.boolean() }),

	'debug-state': z.object({ success: z.boolean() }),
	'event-freeze': z.object({ success: z.boolean() }),
} as const;

export type MessageReturnTypes = {
	[K in Message['kind']]: (typeof MessageReturnSchemas)[K] extends null ? null : z.infer<NonNullable<(typeof MessageReturnSchemas)[K]>>;
};

export const MessagePayloadSchema = MessageSchema.and(
	z.object({
		// messages are each given a unique id so the server may respond with this same id to provide return data
		__message_id: z.string().uuid(),
		// if the user is signed in their token will be passed along for each message
		// token: z.string().optional(),
	}),
);
export type MessagePayload = z.infer<typeof MessagePayloadSchema>;

export const LoginSchema = z.object({
	email: z.string().email(),
	password: z.string().max(64),
	['cf-turnstile-response']: z.string().optional(),
});
export const RegisterSchema = z.object({
	firstName: z.string().nonempty(),
	lastName: z.string().nonempty(),
	email: z
		.string()
		.email()
		.transform((e) => e.toLowerCase()),
	password: z.string().min(8).max(64),
});

export const TokenContentSchema = z.object({
	userId: z.string().uuid(),
	iat: z.number(),
	exp: z.number(),
});

export type TokenContent = z.infer<typeof TokenContentSchema>;
